import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useAuthState } from './AuthGate';
import { api } from '../../lib/api';

interface NavItem {
  to: string;
  label: string;
  section: 'Dashboards' | 'Reports' | 'System';
  adminOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: '/dashboard',        label: 'Org Dashboard',  section: 'Dashboards' },
  { to: '/dashboard/personal', label: 'My Dashboard',   section: 'Dashboards' },
  { to: '/templates',        label: 'Templates',       section: 'Reports' },
  { to: '/ai',               label: 'AI Builder',      section: 'Reports' },
  { to: '/reports',          label: 'Saved Reports',   section: 'Reports' },
  { to: '/schedules',        label: 'Schedules',       section: 'Reports' },
  { to: '/admin',            label: 'Admin',           section: 'System', adminOnly: true },
];

export function Sidebar() {
  const auth = useAuthState();
  const [pendingCount, setPendingCount] = useState<number>(0);

  useEffect(() => {
    if (auth.role !== 'admin') return;
    api.adminListUsers('pending')
      .then(r => setPendingCount(r.users.length))
      .catch(() => {});
  }, [auth.role]);

  const visible = NAV.filter(n => !n.adminOnly || auth.role === 'admin');
  const grouped = visible.reduce<Record<string, NavItem[]>>((acc, n) => {
    (acc[n.section] ??= []).push(n);
    return acc;
  }, {});

  return (
    <aside className="w-[196px] shrink-0 border-r border-border bg-surface flex flex-col">
      <div className="px-4 py-5 border-b border-border-light">
        <div className="text-base font-semibold text-text-primary leading-tight">LAOSS</div>
        <div className="text-[10px] uppercase tracking-[1px] text-text-hint">Reports</div>
      </div>

      <nav className="flex-1 px-2 py-3 space-y-4 overflow-y-auto">
        {Object.entries(grouped).map(([section, items]) => (
          <div key={section}>
            <div className="text-[10px] uppercase tracking-wider text-text-hint px-2 mb-1">{section}</div>
            <div className="space-y-0.5">
              {items.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/dashboard'}
                  className={({ isActive }) => clsx(
                    'flex items-center justify-between px-2.5 py-1.5 rounded-md text-sm transition-colors',
                    isActive
                      ? 'bg-accent-muted text-accent-text font-medium'
                      : 'text-text-muted hover:bg-[#FAF8F5] hover:text-text-secondary',
                  )}
                >
                  <span>{item.label}</span>
                  {item.to === '/admin' && pendingCount > 0 && (
                    <span className="bg-accent text-white text-[10px] px-1.5 py-0.5 rounded-md">{pendingCount}</span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="px-3 py-3 border-t border-border-light">
        <div className="text-xs text-text-secondary truncate">{auth.displayName}</div>
        <div className="text-[10px] text-text-muted truncate">{auth.email}</div>
      </div>
    </aside>
  );
}
